import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useState, useMemo, useEffect, useRef } from "react";
import { createMeshAddress, getExternalPlatformIdFromInternalPlatformId, getPlatformIdFromPlatform, Platform, } from "@orb-labs/types";
import { useExternalAccounts, OAuthPermissionLevel } from "../../hooks/useExternalAccounts.js";
import { capture } from "../../observability/capture.js";
import { isMeshAccessTokenExpired, isMeshRefreshTokenExpired } from "./expiry.js";
import { verifyExchange, refreshAccessToken, fetchBrokerHoldings, previewTransfer, executeTransfer } from "./api.js";
import { MeshEvents } from "./types.js";
import { parseConnectionPayload } from "./parsePayload.js";
const EMPTY_MESH_RESULT = {
    success: false,
    email: null,
    firstName: null,
    lastName: null,
};
const MeshContext = createContext(undefined);
export const useMesh = () => {
    const context = useContext(MeshContext);
    if (!context) {
        throw new Error("useMesh must be used within a MeshProvider");
    }
    return context;
};
export const MeshProvider = ({ storage, meshRuntime, children }) => {
    const [isMeshOpen, _setIsMeshOpen] = useState(false);
    const isMeshOpenRef = useRef(false);
    const setIsMeshOpen = useCallback((v) => {
        isMeshOpenRef.current = v;
        _setIsMeshOpen(v);
    }, []);
    const [isLoading, setIsLoading] = useState(false);
    const [linkedAccounts, setLinkedAccounts] = useState([]);
    const [meshHoldings, setMeshHoldings] = useState([]);
    const [meshHoldingsLoaded, setMeshHoldingsLoaded] = useState(false);
    const { initiateOAuthFlow } = useExternalAccounts();
    const meshPlatformId = useMemo(() => String(getPlatformIdFromPlatform(Platform.MESH_CONNECT)), []);
    // Promise resolve/reject for the openMeshLink() Promise. The runtime adapter
    // fires the callbacks; the shared provider resolves the outer Promise.
    const connectionPromiseRef = useRef(null);
    const meshRefreshInProgressRef = useRef(false);
    const lastMeshRefreshAtRef = useRef(0);
    // ---------------------------------------------------------------------------
    // Mesh API context (derived from runtime config)
    // ---------------------------------------------------------------------------
    const meshApiCtx = useMemo(() => {
        if (!meshRuntime)
            return undefined;
        return {
            apiBaseUrl: meshRuntime.config.apiBaseUrl,
            headers: {
                "Content-Type": "application/json",
                "X-Client-Id": meshRuntime.config.clientId,
                "X-Client-Secret": meshRuntime.config.clientSecret,
            },
        };
    }, [meshRuntime]);
    // ---------------------------------------------------------------------------
    // Linked accounts
    // ---------------------------------------------------------------------------
    const refreshLinkedAccounts = useCallback(async () => {
        if (!meshApiCtx)
            return;
        try {
            const storageKeys = await storage.getLinkedMeshAccountKeys();
            if (storageKeys.length === 0) {
                setLinkedAccounts([]);
                return;
            }
            const accounts = [];
            for (const storageKey of storageKeys) {
                const token = await storage.getMeshToken(storageKey);
                if (!token)
                    continue;
                const requiresReauthentication = isMeshRefreshTokenExpired(token);
                accounts.push({
                    platformId: storageKey,
                    brokerType: token.brokerType,
                    brokerName: token.brokerName,
                    address: token.address,
                    // Legacy entries predate the `linkedAt` field — use the current load
                    // time rather than `expiresAt` (which is in the future and would
                    // render a nonsense "linked at" date and break sort order).
                    linkedAt: new Date(token.linkedAt ?? Date.now()),
                    requiresReauthentication,
                    brokerLogoUrl: token.brokerLogoUrl,
                });
            }
            setLinkedAccounts(accounts);
        }
        catch (error) {
            console.error("[MeshProvider] Failed to refresh linked accounts:", error);
        }
    }, [storage, meshApiCtx]);
    // ---------------------------------------------------------------------------
    // Token refresh
    // ---------------------------------------------------------------------------
    const getValidMeshToken = useCallback(async (accountKey) => {
        if (!meshApiCtx)
            return null;
        const token = await storage.getMeshToken(accountKey);
        if (!token)
            return null;
        if (isMeshRefreshTokenExpired(token))
            return null;
        if (!isMeshAccessTokenExpired(token))
            return token;
        const refreshed = await refreshAccessToken(meshApiCtx, token.refreshToken, token.brokerType);
        if (!refreshed)
            return null;
        const updates = {
            accessToken: refreshed.accessToken,
            expiresAt: Date.now() + (refreshed.expiresInSeconds ?? 3600) * 1000,
        };
        if (refreshed.refreshToken) {
            updates.refreshToken = refreshed.refreshToken;
        }
        if (refreshed.refreshTokenExpiresInSeconds) {
            updates.refreshTokenExpiresAt = Date.now() + refreshed.refreshTokenExpiresInSeconds * 1000;
        }
        await storage.updateMeshToken(accountKey, updates);
        return { ...token, ...updates };
    }, [storage, meshApiCtx]);
    // ---------------------------------------------------------------------------
    // Holdings
    // ---------------------------------------------------------------------------
    const fetchHoldings = useCallback(async () => {
        if (!meshApiCtx)
            return [];
        const accountKeys = await storage.getLinkedMeshAccountKeys();
        const holdings = [];
        for (const accountKey of accountKeys) {
            const token = await getValidMeshToken(accountKey);
            if (!token)
                continue;
            const result = await fetchBrokerHoldings(meshApiCtx, {
                accessToken: token.accessToken,
                brokerType: token.brokerType,
                brokerName: token.brokerName,
                accountAddress: token.address,
                accountKey,
            });
            if (result)
                holdings.push(result);
        }
        return holdings;
    }, [getValidMeshToken, storage, meshApiCtx]);
    const refreshMeshHoldings = useCallback(async () => {
        try {
            const holdings = await fetchHoldings();
            setMeshHoldings(holdings);
        }
        catch (error) {
            console.error("[MeshProvider] Failed to refresh Mesh holdings:", error);
        }
        finally {
            setMeshHoldingsLoaded(true);
        }
    }, [fetchHoldings]);
    // ---------------------------------------------------------------------------
    // Safe combined refresh with cooldown
    // ---------------------------------------------------------------------------
    const refreshMeshStateSafely = useCallback(async (force = false) => {
        const now = Date.now();
        const cooldownMs = 8000;
        if (meshRefreshInProgressRef.current)
            return;
        if (!force && now - lastMeshRefreshAtRef.current < cooldownMs)
            return;
        meshRefreshInProgressRef.current = true;
        lastMeshRefreshAtRef.current = now;
        try {
            await refreshLinkedAccounts();
            await refreshMeshHoldings();
        }
        finally {
            meshRefreshInProgressRef.current = false;
        }
    }, [refreshLinkedAccounts, refreshMeshHoldings]);
    // ---------------------------------------------------------------------------
    // Deferred initial load + app-resume subscription
    // ---------------------------------------------------------------------------
    const meshLoadScheduledRef = useRef(false);
    useEffect(() => {
        if (!meshRuntime) {
            // No mesh runtime wired; mark holdings as loaded so consumers
            // don't block on meshHoldingsLoaded forever.
            setMeshHoldingsLoaded(true);
            return;
        }
        if (!meshLoadScheduledRef.current) {
            meshLoadScheduledRef.current = true;
            refreshMeshStateSafely();
        }
        const unsubscribeResume = meshRuntime.subscribeToAppResume(() => {
            if (!isMeshOpenRef.current) {
                refreshMeshStateSafely();
            }
        });
        return () => {
            unsubscribeResume();
        };
    }, [refreshMeshStateSafely, meshRuntime]);
    // ---------------------------------------------------------------------------
    // Connection result helpers
    // ---------------------------------------------------------------------------
    function resolveConnection(result) {
        if (connectionPromiseRef.current) {
            connectionPromiseRef.current.resolve(result);
            connectionPromiseRef.current = null;
        }
    }
    // ---------------------------------------------------------------------------
    // handleIntegrationConnected (callback from runtime Link adapter)
    // ---------------------------------------------------------------------------
    const handleIntegrationConnected = useCallback(async (payload) => {
        if (!meshApiCtx)
            return;
        try {
            setIsLoading(true);
            const parsed = parseConnectionPayload(payload);
            const { email, firstName, lastName } = await verifyExchange(meshApiCtx, parsed.accessToken);
            const address = createMeshAddress(parsed.brokerType, parsed.accountId);
            const storedToken = {
                accessToken: parsed.accessToken,
                refreshToken: parsed.refreshToken,
                expiresAt: Date.now() + parsed.expiresInSeconds * 1000,
                refreshTokenExpiresAt: Date.now() + parsed.refreshTokenExpiresInSeconds * 1000,
                integrationId: parsed.integrationId,
                address,
                brokerType: parsed.brokerType,
                brokerName: parsed.brokerName,
                brokerLogoUrl: parsed.brokerLogoUrl,
                linkedAt: Date.now(),
            };
            // Treat a persistence failure as a connection failure so the UI
            // never shows "connected" when nothing is stored (reload would lose
            // the token). Finance product — no silent data loss.
            const stored = await storage.storeMeshToken(address, storedToken);
            if (!stored) {
                throw new Error("Failed to persist Mesh connection");
            }
            capture(MeshEvents.MESH_CONNECTION_SUCCESS, {
                broker_type: parsed.brokerType,
                has_email: !!email,
            });
            setIsLoading(false);
            setIsMeshOpen(false);
            resolveConnection({
                success: true,
                email,
                firstName,
                lastName,
                brokerType: parsed.brokerType,
                brokerName: parsed.brokerName,
                accountId: parsed.accountId,
                integrationId: parsed.integrationId,
            });
            await refreshLinkedAccounts();
            await refreshMeshHoldings();
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to process connection";
            console.error("[MeshProvider] Error handling connection:", error);
            capture(MeshEvents.MESH_CONNECTION_FAILED, { error: errorMessage });
            setIsLoading(false);
            // Clear mesh-open state so a failed integration (e.g. storeMeshToken
            // returning false) doesn't leave the concurrent openMeshLink guard
            // stuck — on mobile, the runtime's onIntegrationConnected already
            // closed the Link host before invoking this callback, so handleExit
            // will not fire to clear the flag.
            setIsMeshOpen(false);
            resolveConnection({ ...EMPTY_MESH_RESULT, error: errorMessage });
        }
    }, [meshApiCtx, storage, refreshLinkedAccounts, refreshMeshHoldings, setIsMeshOpen]);
    // ---------------------------------------------------------------------------
    // handleExit (callback from runtime Link adapter)
    // ---------------------------------------------------------------------------
    const handleExit = useCallback((error) => {
        setIsMeshOpen(false);
        // Skip event + resolve if closeMeshLink already claimed ownership (it
        // nulls connectionPromiseRef before tearing down the session to race-proof
        // against SDKs that fire onExit asynchronously from closeLink()).
        if (connectionPromiseRef.current) {
            capture(MeshEvents.MESH_LINK_CLOSED, error ? { error } : undefined);
            resolveConnection({ ...EMPTY_MESH_RESULT, error: error ?? "Connection cancelled by user" });
        }
    }, [setIsMeshOpen]);
    // ---------------------------------------------------------------------------
    // openMeshLink
    // ---------------------------------------------------------------------------
    const openMeshLink = useCallback(async () => {
        if (!meshRuntime || !meshApiCtx) {
            return { ...EMPTY_MESH_RESULT, error: "Mesh runtime not configured" };
        }
        // Guard against concurrent calls — if a link session is already open,
        // don't overwrite the pending promise.
        if (isMeshOpenRef.current) {
            return { ...EMPTY_MESH_RESULT, error: "A Mesh Link session is already open" };
        }
        if (!meshRuntime.config.clientId || !meshRuntime.config.clientSecret) {
            return {
                ...EMPTY_MESH_RESULT,
                error: "Mesh is not configured. Please add MESH_CLIENT_ID and MESH_CLIENT_SECRET to your environment.",
            };
        }
        try {
            setIsLoading(true);
            const sessionId = await storage.getMeshDeviceId();
            const platformId = getExternalPlatformIdFromInternalPlatformId(BigInt(meshPlatformId));
            if (!platformId) {
                throw new Error("Mesh platform ID not found");
            }
            const oauthResponse = await initiateOAuthFlow({
                platformId,
                sessionId,
                permissions: OAuthPermissionLevel.READ_AND_WRITE,
            });
            if (!oauthResponse?.authenticationUrl) {
                setIsLoading(false);
                return { ...EMPTY_MESH_RESULT, error: "Failed to initialize exchange connection" };
            }
            const meshLinkToken = oauthResponse.authTokenOrAuthorizationCode;
            if (!meshLinkToken) {
                setIsLoading(false);
                return { ...EMPTY_MESH_RESULT, error: "Failed to get link token from server" };
            }
            // Create a Promise that resolves when the Link SDK fires onIntegrationConnected or onExit.
            // The resolve/reject are stored in a ref so handleIntegrationConnected/handleExit can fulfill them.
            const resultPromise = new Promise((resolve, reject) => {
                connectionPromiseRef.current = { resolve, reject };
            });
            setIsMeshOpen(true);
            setIsLoading(false);
            capture(MeshEvents.MESH_LINK_OPENED);
            meshRuntime.openMeshLinkSession(meshLinkToken, {
                onIntegrationConnected: handleIntegrationConnected,
                onExit: handleExit,
            });
            return await resultPromise;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to connect to exchange";
            // Clear session state so a synchronous failure inside openMeshLinkSession
            // (SDK init/open error) doesn't leave the concurrent guard stuck — the
            // next openMeshLink call would otherwise see isMeshOpenRef.current=true
            // and bail even though no session is actually running.
            setIsLoading(false);
            setIsMeshOpen(false);
            connectionPromiseRef.current = null;
            return { ...EMPTY_MESH_RESULT, error: errorMessage };
        }
    }, [
        meshRuntime,
        meshApiCtx,
        storage,
        meshPlatformId,
        initiateOAuthFlow,
        handleIntegrationConnected,
        handleExit,
        setIsMeshOpen,
    ]);
    // ---------------------------------------------------------------------------
    // closeMeshLink
    // ---------------------------------------------------------------------------
    const closeMeshLink = useCallback(() => {
        // Claim ownership of the pending promise BEFORE tearing down the session.
        // Some SDK versions fire onExit asynchronously (microtask / postMessage)
        // after closeLink() returns, which would otherwise cause handleExit to
        // fire MESH_LINK_CLOSED a second time. By nulling the ref first, handleExit
        // will see no pending promise and short-circuit.
        const pending = connectionPromiseRef.current;
        connectionPromiseRef.current = null;
        meshRuntime?.closeMeshLinkSession();
        setIsMeshOpen(false);
        if (pending) {
            capture(MeshEvents.MESH_LINK_CLOSED);
            pending.resolve({ ...EMPTY_MESH_RESULT, error: "Connection cancelled by user" });
        }
    }, [meshRuntime, setIsMeshOpen]);
    // ---------------------------------------------------------------------------
    // Transfer (preview → execute → optional MFA retry)
    // ---------------------------------------------------------------------------
    const openMeshTransfer = useCallback(async (params) => {
        if (!meshApiCtx || !meshRuntime) {
            throw new Error("Mesh runtime not configured");
        }
        const token = await getValidMeshToken(params.from);
        if (!token) {
            throw new Error("Mesh token expired. Please reconnect your exchange account.");
        }
        capture(MeshEvents.MESH_TRANSFER_INITIATED, { asset: params.asset });
        const previewId = await previewTransfer(meshApiCtx, {
            accessToken: token.accessToken,
            brokerType: token.brokerType,
            symbol: params.asset,
            amount: Number(params.amount),
            toAddress: params.destinationAddress,
            networkId: params.networkId,
        });
        let result = await executeTransfer(meshApiCtx, {
            accessToken: token.accessToken,
            brokerType: token.brokerType,
            previewId,
        });
        if (result.kind === "mfaRequired") {
            const userMfaCode = await meshRuntime.requestMeshMfaCode({
                brokerName: token.brokerName || token.brokerType || "your exchange",
                mfaType: result.mfaType,
            });
            result = await executeTransfer(meshApiCtx, {
                accessToken: token.accessToken,
                brokerType: token.brokerType,
                previewId,
                mfaCode: userMfaCode,
            });
        }
        if (result.kind === "error") {
            capture(MeshEvents.MESH_TRANSFER_FAILED, { error: result.message });
            throw new Error(result.message);
        }
        if (result.kind === "mfaRequired") {
            capture(MeshEvents.MESH_TRANSFER_FAILED, { error: "MFA required after retry" });
            throw new Error("Verification code was incorrect. Please try again.");
        }
        capture(MeshEvents.MESH_TRANSFER_COMPLETED, { transfer_id: result.transferId });
        await refreshMeshHoldings();
        return {
            transferId: result.transferId,
            txHash: result.txHash,
            status: result.status,
        };
    }, [getValidMeshToken, meshApiCtx, meshRuntime, refreshMeshHoldings]);
    // ---------------------------------------------------------------------------
    // Unlink
    // ---------------------------------------------------------------------------
    const unlinkAccount = useCallback(async (storageKey) => {
        const token = await storage.getMeshToken(storageKey);
        await storage.clearMeshToken(storageKey);
        capture(MeshEvents.MESH_ACCOUNT_UNLINKED, {
            account_id: token?.address ?? storageKey,
        });
        await refreshLinkedAccounts();
        await refreshMeshHoldings();
    }, [storage, refreshLinkedAccounts, refreshMeshHoldings]);
    // ---------------------------------------------------------------------------
    // Reset (logout)
    // ---------------------------------------------------------------------------
    const reset = useCallback(async () => {
        await storage.clearAllMeshTokens();
        await storage.clearMeshDeviceId();
        setLinkedAccounts([]);
        setMeshHoldings([]);
    }, [storage]);
    // ---------------------------------------------------------------------------
    // Context value
    // ---------------------------------------------------------------------------
    const contextValue = useMemo(() => ({
        openMeshLink,
        isMeshOpen,
        closeMeshLink,
        linkedAccounts,
        meshHoldings,
        refreshLinkedAccounts,
        refreshMeshHoldings,
        unlinkAccount,
        openMeshTransfer,
        isLoading,
        meshHoldingsLoaded,
        reset,
    }), [
        openMeshLink,
        isMeshOpen,
        closeMeshLink,
        linkedAccounts,
        meshHoldings,
        meshHoldingsLoaded,
        refreshLinkedAccounts,
        refreshMeshHoldings,
        unlinkAccount,
        openMeshTransfer,
        isLoading,
        reset,
    ]);
    return _jsx(MeshContext.Provider, { value: contextValue, children: children });
};
